import React, { memo } from 'react';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import Checkbox from '@lib/components/CheckBox/CheckBox';
import { UseBulkSelectResult } from '@lib/hooks/useTableItemsBulkSelect';
import { isItemsMatch } from '@lib/components/Table/utils';
import { BaseItem, TableProps } from '../../types';
import RowActionsMenuCell from './RowActionsMenuCell/RowActionsMenuCell';
import RowActionsCell from './RowActionsCell/RowActionsCell';
import TableBodyCell from './TableBodyCell';
import styles from './TableBody.module.scss';

interface Props<T extends BaseItem> {
  hasActionsColumn: boolean;
  hasActionsMenuColumn: boolean;
  hasSelectColumn: boolean;
  rowItem: T;
}

function TableBodyRow<T extends BaseItem>(props: Props<T> & TableProps<T>) {
  const {
    hasActionsColumn,
    hasActionsMenuColumn,
    hasSelectColumn,
    rowItem,
    actions,
    actionsMenu,
    columns,
    getIsRowDisabled,
    getIsRowSelected = () => false,
    getRowClassName,
    onRowClick,
    useBulkSelectResult = {} as UseBulkSelectResult<T>,
  } = props;

  const {
    isAllTableItemsSelected,
    selectedTableItems,
    handleSelectTableItem,
    handleUnselectTableItem,
    unselectedTableItems,
  } = useBulkSelectResult;
  const isDisabled = getIsRowDisabled && getIsRowDisabled(rowItem);
  const isSelectedByBulkSelect =
    (isAllTableItemsSelected &&
      !unselectedTableItems?.some((selectedItem) =>
        isItemsMatch(selectedItem, rowItem),
      )) ||
    selectedTableItems?.some((selectedItem) =>
      isItemsMatch(selectedItem, rowItem),
    );
  const handleRowCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (event.target.checked) {
      handleSelectTableItem(rowItem);
    } else {
      handleUnselectTableItem(rowItem);
    }
  };

  return (
    <tr
      data-key={rowItem.id}
      className={classNames(
        {
          // [styles.disabled]: isDisabled,
          [styles.trSelected]:
            isSelectedByBulkSelect || getIsRowSelected(rowItem),
          [styles.trClickable]: onRowClick,
        },
        getRowClassName && getRowClassName(rowItem),
      )}
      onClick={onRowClick ? () => onRowClick(rowItem) : undefined}
    >
      {hasSelectColumn && (
        <td className={styles.selectCell}>
          <div className={styles.tdContent}>
            <Checkbox
              id={`table-body-checkbox-${rowItem.id}`}
              name={`table-body-checkbox-${rowItem.id}`}
              checked={isSelectedByBulkSelect}
              onChange={handleRowCheckboxChange}
              disabled={isDisabled}
            />
          </div>
        </td>
      )}
      {columns.map((column) => (
        <TableBodyCell<T>
          key={column?.id || uuidv4()}
          column={column}
          isDisabled={isDisabled}
          rowItem={rowItem}
        />
      ))}
      {hasActionsColumn && <RowActionsCell actions={actions} item={rowItem} />}
      {hasActionsMenuColumn && (
        <RowActionsMenuCell actionsMenu={actionsMenu} item={rowItem} />
      )}
    </tr>
  );
}

export default memo(TableBodyRow) as <T extends BaseItem>(
  props: Props<T> & TableProps<T>,
) => React.ReactElement;
